import React, { useState, useEffect, useCallback } from'react';
import { useTranslation } from'@/store/languageStore';
import { dashboardService, DashboardWidget, DashboardLayout } from'@/services/dashboard/dashboardService';
import WidgetContainer from'@/components/dashboard/WidgetContainer';
import WidgetSelector from'@/components/dashboard/WidgetSelector';
import VehicleCountWidget from'@/components/dashboard/widgets/VehicleListWidget';
import InspectionDueWidget from'@/components/dashboard/widgets/InspectionDueWidget';
import LeasingExpiringWidget from'@/components/dashboard/widgets/LeasingExpiringWidget';
import TaxDueWidget from '@/components/dashboard/widgets/TaxDueWidget';
import UnpluggedWidget from'@/components/dashboard/widgets/UnpluggedWidget';
import LowBatteryWidget from'@/components/dashboard/widgets/LowBatteryWidget';
import VehicleStatusWidget from'@/components/dashboard/widgets/VehicleStatusWidget';
import ServiceDueWidget from'@/components/dashboard/widgets/ServiceDueWidget';
import ServiceCostWidget from'@/components/dashboard/widgets/ServiceCostWidget';
import MyAssignmentsWidget from'@/components/dashboard/widgets/MyAssignmentsWidget';
import MyBookingsWidget from'@/components/dashboard/widgets/MyBookingsWidget';
import GpsDataLagWidget from '@/components/dashboard/widgets/GpsDataLagWidget';
import FavoriteModulesBar from '@/components/dashboard/FavoriteModulesBar';
import { PlusIcon, Squares2X2Icon, PencilIcon, CheckIcon, XMarkIcon } from'@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable, DropResult } from'@hello-pangea/dnd';
import toast from'react-hot-toast';
import { usePermissions } from '@/hooks/usePermissions';
import { getDashboardWidgetFeatureCode, GLOBAL_ADMIN_ONLY_DASHBOARD_WIDGETS } from '@/constants/dashboardWidgets';
import { useAuthStore } from '@/store/authStore';
import { isGlobalAdminUser } from '@/utils/isGlobalAdminUser';
const widgetComponents: Record<string, React.FC<{ onRemove?: () => void }>> = {
'vehicle-count': VehicleCountWidget,
'inspection-due': InspectionDueWidget,
'leasing-expiring': LeasingExpiringWidget,
'tax-due': TaxDueWidget,
'unplugged-vehicles': UnpluggedWidget,
'low-battery': LowBatteryWidget,
'vehicle-status': VehicleStatusWidget,
'service-due': ServiceDueWidget,
'service-cost': ServiceCostWidget,
'my-assignments': MyAssignmentsWidget,
'my-bookings': MyBookingsWidget,
'gps-data-lag': GpsDataLagWidget,
};
const COLUMNS = [0, 1, 2] as const;
const Dashboard: React.FC = () => {
const { t } = useTranslation();
const user = useAuthStore((s) => s.user);
const { hasActionAccess, isUnlimited, isLoading: permissionsLoading } = usePermissions();
const canShowWidget = (widgetType: string) => {
  if (GLOBAL_ADMIN_ONLY_DASHBOARD_WIDGETS.has(widgetType)) {
    return isGlobalAdminUser(user);
  }
  const featureCode = getDashboardWidgetFeatureCode(widgetType);
  if (!featureCode) return true;
  if (isUnlimited) return true;
  if (permissionsLoading) return false;
  return hasActionAccess(featureCode, 'VIEW');
};
const [layout, setLayout] = useState<DashboardLayout>({ widgets: [] });
const [draftLayout, setDraftLayout] = useState<DashboardLayout>({ widgets: [] });
const [loading, setLoading] = useState(true);
const [editMode, setEditMode] = useState(false);
const [selectorOpen, setSelectorOpen] = useState(false);
const [saving, setSaving] = useState(false);
const activeLayout = editMode ? draftLayout : layout;
useEffect(() => {
loadLayout();
}, []);
const loadLayout = async () => {
try {
setLoading(true);
const data = await dashboardService.getLayout();
setLayout(data);
} catch {
setLayout({ widgets: [] });
} finally {
setLoading(false);
}
};
const enterEditMode = () => {
setDraftLayout(JSON.parse(JSON.stringify(layout)));
setEditMode(true);
};
const cancelEditMode = () => {
setEditMode(false);
setDraftLayout({ widgets: [] });
};
const saveAndExit = async () => {
try {
setSaving(true);
await dashboardService.saveLayout(draftLayout);
setLayout(JSON.parse(JSON.stringify(draftLayout)));
setEditMode(false);
toast.success(t('dashboard.layoutSaved'));
} catch {
toast.error(t('dashboard.layoutSaveFailed'));
} finally {
setSaving(false);
}
};
const handleAddWidget = (widgetType: string, column: number) => {
const targetLayout = editMode ? draftLayout : layout;
const newWidget: DashboardWidget = {
id: `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
widgetType,
column,
order: targetLayout.widgets.filter(w => w.column === column).length,
};
const newLayout = {
...targetLayout,
widgets: [...targetLayout.widgets, newWidget],
};
if (editMode) {
setDraftLayout(newLayout);
} else {
setLayout(newLayout);
dashboardService.saveLayout(newLayout).catch(() => {
toast.error(t('dashboard.layoutSaveFailed'));
});
}
toast.success(t('dashboard.widgetAdded'));
};
const handleRemoveWidget = (widgetId: string) => {
if (editMode) {
setDraftLayout(prev => ({
...prev,
widgets: prev.widgets.filter(w => w.id !== widgetId),
}));
} else {
const newLayout = { ...layout, widgets: layout.widgets.filter(w => w.id !== widgetId) };
setLayout(newLayout);
dashboardService.saveLayout(newLayout).catch(() => {
toast.error(t('dashboard.layoutSaveFailed'));
});
toast.success(t('dashboard.widgetRemoved'));
}
};
const getWidgetsForColumn = useCallback((column: number) => {
return activeLayout.widgets
.filter(w => w.column === column)
.sort((a, b) => a.order - b.order);
}, [activeLayout]);
const handleDragEnd = (result: DropResult) => {
const { source, destination } = result;
if (!destination) return;
if (source.droppableId === destination.droppableId && source.index === destination.index) return;
const sourceCol = parseInt(source.droppableId.replace('column-',''));
const destCol = parseInt(destination.droppableId.replace('column-',''));
const allWidgets = [...draftLayout.widgets];
const sourceWidgets = allWidgets
.filter(w => w.column === sourceCol)
.sort((a, b) => a.order - b.order);
const [movedWidget] = sourceWidgets.splice(source.index, 1);
movedWidget.column = destCol;
if (sourceCol === destCol) {
sourceWidgets.splice(destination.index, 0, movedWidget);
sourceWidgets.forEach((w, i) => { w.order = i; });
} else {
const destWidgets = allWidgets
.filter(w => w.column === destCol && w.id !== movedWidget.id)
.sort((a, b) => a.order - b.order);
destWidgets.splice(destination.index, 0, movedWidget);
sourceWidgets.forEach((w, i) => { w.order = i; });
destWidgets.forEach((w, i) => { w.order = i; });
}
setDraftLayout({ widgets: allWidgets });
};
const renderWidget = (widget: DashboardWidget, index: number) => {
const WidgetComponent = widgetComponents[widget.widgetType];
const allowed = canShowWidget(widget.widgetType);
if (editMode) {
return (
<Draggable key={widget.id} draggableId={widget.id} index={index}>
{(provided, snapshot) => (
<div
ref={provided.innerRef}
{...provided.draggableProps}
className={`mb-4 transition-shadow ${snapshot.isDragging ?'shadow-2xl z-50' :''}`}
>
<WidgetContainer
editMode
onRemove={() => handleRemoveWidget(widget.id)}
dragHandleProps={provided.dragHandleProps ?? undefined}
>
{!allowed ? (
<div className="text-textTertiary text-center py-8 text-sm">
{t('dashboard.widgetNoPermission')}
</div>
) : WidgetComponent ? (
<WidgetComponent />
) : (
<div className="text-textTertiary text-center py-8">
{t('dashboard.widgetNotAvailable')}
</div>
)}
</WidgetContainer>
</div>
)}
</Draggable>
);
}
if (!allowed) {
return null;
}
return (
<WidgetContainer key={widget.id} onRemove={() => handleRemoveWidget(widget.id)}>
{WidgetComponent ? (
<WidgetComponent onRemove={() => handleRemoveWidget(widget.id)} />
) : (
<div className="text-textTertiary text-center py-8">
{t('dashboard.widgetNotAvailable')}
</div>
)}
</WidgetContainer>
);
};
const renderColumn = (column: number) => {
const widgets = getWidgetsForColumn(column);
if (editMode) {
return (
<Droppable droppableId={`column-${column}`} key={column}>
{(provided, snapshot) => (
<div
ref={provided.innerRef}
{...provided.droppableProps}
className={`space-y-0 min-h-[120px] rounded-xl p-2 transition-colors ${
snapshot.isDraggingOver
?'bg-blue-50/50 dark:bg-blue-900/10 ring-2 ring-blue-300 dark:ring-blue-700 ring-dashed'
:'bg-transparent'
}`}
>
{widgets.map((widget, index) => renderWidget(widget, index))}
{provided.placeholder}
{widgets.length === 0 && !snapshot.isDraggingOver && (
<div className="border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-xl p-8 text-center">
<span className="text-blue-400 dark:text-blue-500 text-sm">{t('dashboard.dragHere')}</span>
</div>
)}
<button
onClick={() => setSelectorOpen(true)}
className="w-full border-2 border-dashed border-borderDefault rounded-xl p-3 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/5 transition-colors mt-2"
>
<PlusIcon className="w-5 h-5 mx-auto text-textTertiary"/>
</button>
</div>
)}
</Droppable>
);
}
return (
<div className="space-y-6"key={column}>
{widgets.map((widget, index) => renderWidget(widget, index))}
{widgets.length === 0 && (
<div
className="border-2 border-dashed border-borderDefault rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/5 transition-colors"
onClick={() => { enterEditMode(); setSelectorOpen(true); }}
>
<PlusIcon className="w-8 h-8 mx-auto text-textTertiary mb-2"/>
<span className="text-textTertiary text-sm">{t('dashboard.addWidget')}</span>
</div>
)}
</div>
);
};
const renderEmptyDashboard = () => (
<div className="flex flex-col items-center justify-center h-[60vh] text-center">
<div className="p-6 rounded-full bg-bgElevated mb-6">
<Squares2X2Icon className="w-16 h-16 text-textTertiary"/>
</div>
<h2 className="text-2xl font-semibold text-textDefault mb-2">
{t('dashboard.emptyDashboard')}
</h2>
<p className="text-textSecondary mb-6 max-w-md">
{t('dashboard.emptyDashboardDescription')}
</p>
<button
onClick={() => { enterEditMode(); setSelectorOpen(true); }}
className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-lg"
>
<PlusIcon className="w-5 h-5"/>
{t('dashboard.addFirstWidget')}
</button>
</div>
);
if (loading) {
return (
<div className="flex items-center justify-center h-full">
<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
</div>
);
}
return (
<div className="p-6 h-full overflow-auto bg-bgContainer">
{/* Header */}
<div className="flex items-center justify-between mb-6">
<h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
<Squares2X2Icon className="w-6 h-6 text-primary"/>
{editMode ? t('dashboard.editDashboard') : (t('dashboard.title') ||'Dashboard')}
</h1>
<div className="flex items-center gap-2">
{editMode ? (
<>
<button
onClick={cancelEditMode}
className="flex items-center gap-2 px-4 py-2 bg-bgBase hover:bg-bgBase text-textDefault rounded-lg font-medium transition-colors"
>
<XMarkIcon className="w-5 h-5"/>
{t('dashboard.cancel')}
</button>
<button
onClick={saveAndExit}
disabled={saving}
className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors shadow-md disabled:opacity-50"
>
{saving ? (
<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"/>
) : (
<CheckIcon className="w-5 h-5"/>
)}
{t('dashboard.save')}
</button>
</>
) : (
<button
onClick={enterEditMode}
className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-md"
>
<PencilIcon className="w-5 h-5"/>
{t('dashboard.edit')}
</button>
)}
</div>
</div>
<FavoriteModulesBar editMode={editMode} />
{/* Dashboard content */}
{activeLayout.widgets.length === 0 && !editMode ? (
renderEmptyDashboard()
) : editMode ? (
<DragDropContext onDragEnd={handleDragEnd}>
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
{COLUMNS.map(col => renderColumn(col))}
</div>
</DragDropContext>
) : (
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
{COLUMNS.map(col => renderColumn(col))}
</div>
)}
<WidgetSelector
isOpen={selectorOpen}
onClose={() => setSelectorOpen(false)}
onSelectWidget={handleAddWidget}
existingWidgetTypes={activeLayout.widgets.map(w => w.widgetType)}
/>
{saving && (
<div className="fixed bottom-4 right-4 bg-bgElevated shadow-lg rounded-lg px-4 py-2 flex items-center gap-2 text-sm text-textSecondary">
<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
{t('dashboard.saving')}
</div>
)}
</div>
);
};
export default Dashboard;
